import { useRef, useEffect, useState } from "react";
import styles from "./OtpVerificationInput.module.scss";
import { classNames } from "primereact/utils";

export const OTP_CODE_LENGTH = 6;

interface IOtpVerificationInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

export const OtpVerificationInput = ({
  value,
  onChange,
  onComplete,
  disabled,
  error,
}: IOtpVerificationInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, OTP_CODE_LENGTH);
    onChange(digits);
    if (digits.length === OTP_CODE_LENGTH) {
      onComplete(digits);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/^\d$/.test(e.key) &&
      !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Enter"].includes(e.key) &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_CODE_LENGTH);
    onChange(digits);
    if (digits.length === OTP_CODE_LENGTH) {
      onComplete(digits);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.boxes} aria-hidden="true">
        {Array.from({ length: OTP_CODE_LENGTH }, (_, i) => (
          <div
            key={i}
            className={classNames(styles.box, {
              [styles.boxError]: error,
              [styles.boxFilled]: i < value.length,
              [styles.boxActive]: isFocused && i === value.length && value.length < OTP_CODE_LENGTH,
            })}
          >
            <span className={styles.digit}>{value[i] ?? ""}</span>
          </div>
        ))}
      </div>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={OTP_CODE_LENGTH}
        pattern={`\\d{${OTP_CODE_LENGTH}}`}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        readOnly={disabled}
        aria-disabled={disabled}
        className={styles.input}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
      {disabled && (
        <div className={styles.loaderOverlay} aria-hidden="true">
          <span className={styles.spinner} />
        </div>
      )}
    </div>
  );
};
